import type { Fixture, KnockoutNode, Match, Team } from '@/types/tournament.model';
import { allGroupMatchesResolved } from '@/utils/fixture-utils';

/** Equipo aún no definido en el cuadro (solo posición de grupo, ej. "2° Grupo C"). */
export function isPlaceholderGroupSeedName(name: string): boolean {
  return /^\d+[°º]?\s*Grupo\b/i.test(name.trim());
}

export function teamIsRealForTvKnockout(
  team: Team | undefined,
  playersPerTeam: number,
): boolean {
  if (!team?.players?.length) return false;
  const withName = team.players.filter((p) => p?.name?.trim());
  if (withName.length < playersPerTeam) return false;
  if (withName.every((p) => isPlaceholderGroupSeedName(p.name!))) return false;
  return true;
}

/** Partido de eliminatoria con ambos equipos ya materializados (no solo seeds de grupo). */
export function isRealKnockoutMatch(match: Match): boolean {
  const n = match.playersPerTeam ?? 2;
  if (!match.team1 || !match.team2) return false;
  return (
    teamIsRealForTvKnockout(match.team1, n) &&
    teamIsRealForTvKnockout(match.team2, n)
  );
}

function walkKnockoutNodes(
  node: KnockoutNode | null | undefined,
  visit: (n: KnockoutNode) => void,
): void {
  if (!node) return;
  visit(node);
  walkKnockoutNodes(node.leftChild, visit);
  walkKnockoutNodes(node.rightChild, visit);
}

export function fixtureHasRealKnockoutMatch(fixture: Fixture | null): boolean {
  const root = fixture?.knockoutTree?.rootNode;
  if (!root) return false;
  let found = false;
  walkKnockoutNodes(root, (n) => {
    if (n.match && isRealKnockoutMatch(n.match)) found = true;
  });
  return found;
}

export type TvFixturePresentation =
  | 'groups-only'
  | 'knockout-only'
  | 'split-groups-knockout';

/**
 * Modo TV: no mostrar eliminatoria hasta que haya al menos un cruce real (no bye, no solo seeds).
 * Reparto temporal en columna única: 100% grupos → 70/30 → 100% eliminatorias.
 */
export function resolveTvFixturePresentation(
  fixture: Fixture | null,
): TvFixturePresentation {
  const hasGroups = (fixture?.groupList?.length ?? 0) > 0;
  const hasRealKo = fixtureHasRealKnockoutMatch(fixture);
  const groupsDone = allGroupMatchesResolved(fixture);

  if (!hasRealKo) {
    return 'groups-only';
  }
  if (hasGroups && !groupsDone) {
    return 'split-groups-knockout';
  }
  return 'knockout-only';
}
